import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DigisubservicesService } from '../services/digisubservices.service';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-yenebaki-offwise-jahirata',
  templateUrl: './yenebaki-offwise-jahirata.component.html',
  styleUrl: './yenebaki-offwise-jahirata.component.scss'
})

export class YenebakiOffwiseJahirataComponent {

  reportType: any;
  startDate: any;
  endDate: any;
  repName: any;
  offRepName: any;
  currentDate: Date = new Date();
  isLoading = true;
  isDateSelected: boolean = false;

  allYears: string[] = [];
  earlyYears: string[] = [];
  recentYears: string[] = [];

  onlyOutstandingVB: any[] = [];

  constructor(
    private dialogRef: MatDialogRef<YenebakiOffwiseJahirataComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private service: DigisubservicesService,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe
  ) { }

  ngOnInit(): void {
    this.reportType = this.data['reportType'];
    this.startDate = this.data['startDate'] || '';
    this.endDate = this.data['endDate'] || '';
    this.repName = this.data['repName'] || 'ALL';
    this.offRepName = this.data['offRepName'] || 'ALL';

    this.checkDateSelection();

    const req = {
      rep_name: this.repName,
      start_date: this.startDate,
      end_date: this.endDate,
      off_rep_name: this.offRepName,
      advt_name: this.data['name'] || 'ALL'
    };

    this.service.getBusinessOutstanding(req).subscribe({
      next: (response: any) => {
        const actualData: any[] = response?.data ?? [];
        console.log('API raw data:', actualData);

        if (actualData.length > 0) {
          this.processData(actualData);
        } else {
          this.onlyOutstandingVB = [];
          this.allYears = [];
          this.earlyYears = [];
          this.recentYears = [];
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('API Error:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  processData(rawData: any[]): void {

    // ── Step 1: collect & sort all financial years ───────────────────────────
    const yearSet = new Set<string>();
    rawData.forEach(yg => {
      const y = (yg.fin_year || '').trim();
      if (y) yearSet.add(y);
    });

    this.allYears = Array.from(yearSet).sort();
    const SPLIT_YEAR = '2021-22';
    this.earlyYears = this.allYears.filter(y => y < SPLIT_YEAR);
    this.recentYears = this.allYears.filter(y => y >= SPLIT_YEAR);

    // ── Step 2: pivot year-first → offRep-first using Maps ──────────────────
    const offRepMap = new Map<string, any>();

    rawData.forEach(yg => {
      const finYear = (yg.fin_year || '').trim();

      (yg.off_rep_data || []).forEach((off: any) => {
        const offName = (off.off_rep_name || 'Unassigned').trim();

        if (!offRepMap.has(offName)) {
          offRepMap.set(offName, {
            off_rep_name: offName,
            office_rep_mob: off.office_rep_mob || '',
            repMap: new Map<string, any>()
          });
        }
        const offEntry = offRepMap.get(offName);

        (off.rep_data || []).forEach((rep: any) => {
          const repKey = (rep.rep_name || 'Unassigned').trim();

          if (!offEntry.repMap.has(repKey)) {
            offEntry.repMap.set(repKey, {
              rep_name: repKey,
              rep_mob: rep.rep_mob || '',
              location: rep.location || '',
              pending: {} as Record<string, number>,
              billing: {} as Record<string, number>
            });
          }
          const repEntry = offEntry.repMap.get(repKey);

          let yearBal = 0;
          let yearBilling = 0;
          (rep.advtdata || []).forEach((adv: any) => {
            yearBal     += Number(adv.contract_bal_amt) || 0;
            yearBilling += Number(adv.contract_net_amt) || 0;
          });

          repEntry.pending[finYear] = (repEntry.pending[finYear] || 0) + yearBal;
          repEntry.billing[finYear] = (repEntry.billing[finYear] || 0) + yearBilling;
        });
      });
    });

    // ── Step 3: compute subtotals & flatten Maps → arrays ───────────────────
    this.onlyOutstandingVB = Array.from(offRepMap.values()).map(off => {

      const rep_data = Array.from(off.repMap.values()).map((rep: any) => {
        const total_18_21 = this.earlyYears
          .reduce((s: number, y: string) => s + (rep.pending[y] || 0), 0);

        const grand_total = this.allYears
          .reduce((s: number, y: string) => s + (rep.pending[y] || 0), 0);

        return { ...rep, total_18_21, grand_total };
      });

      return {
        off_rep_name: off.off_rep_name,
        office_rep_mob: off.office_rep_mob,
        rep_data
      };
    });

    console.log('Pivoted data:', this.onlyOutstandingVB);
  }

  // ── Office-rep billing column totals ─────────────────────────────────────
  getOffRepBilling(offRep: any, year: string): number {
    return (offRep.rep_data || [])
      .reduce((s: number, r: any) => s + (r.billing[year] || 0), 0);
  }

  getOffRepEarlyBilling(offRep: any): number {
    return this.earlyYears
      .reduce((s: number, y: string) => s + this.getOffRepBilling(offRep, y), 0);
  }

  getOffRepGrandBilling(offRep: any): number {
    return this.allYears
      .reduce((s: number, y: string) => s + this.getOffRepBilling(offRep, y), 0);
  }

  // ── Grand billing totals row ─────────────────────────────────────────────
  getGrandBilling(year: string): number {
    return this.onlyOutstandingVB
      .reduce((s: number, off: any) => s + this.getOffRepBilling(off, year), 0);
  }

  getGrandEarlyBilling(): number {
    return this.earlyYears
      .reduce((s: number, y: string) => s + this.getGrandBilling(y), 0);
  }

  getGrandOverallBilling(): number {
    return this.allYears
      .reduce((s: number, y: string) => s + this.getGrandBilling(y), 0);
  }

  // ── Office-rep column totals ──────────────────────────────────────────────
  getOffRepTotal(offRep: any, year: string): number {
    return (offRep.rep_data || [])
      .reduce((s: number, r: any) => s + (r.pending[year] || 0), 0);
  }

  getOffRepEarlyTotal(offRep: any): number {
    return this.earlyYears
      .reduce((s: number, y: string) => s + this.getOffRepTotal(offRep, y), 0);
  }

  getOffRepGrandTotal(offRep: any): number {
    return this.allYears
      .reduce((s: number, y: string) => s + this.getOffRepTotal(offRep, y), 0);
  }

  // ── Grand totals row ──────────────────────────────────────────────────────
  getGrandTotal(year: string): number {
    return this.onlyOutstandingVB
      .reduce((s: number, off: any) => s + this.getOffRepTotal(off, year), 0);
  }

  getGrandEarlyTotal(): number {
    return this.earlyYears
      .reduce((s: number, y: string) => s + this.getGrandTotal(y), 0);
  }

  getGrandOverallTotal(): number {
    return this.allYears
      .reduce((s: number, y: string) => s + this.getGrandTotal(y), 0);
  }

  // ── Compact year-range label: "2014-15","2020-21" → "2014-21" ───────────
  getYearShort(startYear: string, endYear: string): string {
    const start = (startYear || '').split('-')[0];       // "2014"
    const end   = (endYear   || '').split('-')[1] || ''; // "21"
    return `${start}-${end}`;
  }

  // ── Format amount — no decimals ──────────────────────────────────────────
  formatCurrency(value: number | string | null | undefined): string {
    if (value === null || value === undefined) return '';
    const num = Math.round(Number(value));
    if (isNaN(num) || num === 0) return '';
    return num.toString();
  }

  // ── Format percentage — year amount as % of billing total ───────────────
  formatPercent(yearAmount: number, rowGrandTotal: number): string {
    if (!rowGrandTotal || rowGrandTotal === 0) return '';
    const pct = (yearAmount / rowGrandTotal) * 100;
    if (pct === 0) return '';
    return Math.round(pct) + '%';
  }

  exportExcel(fileName: string): void {
    const element = document.getElementById('print-section');
    if (!element) return;
    try {
      const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element);
      const wb: XLSX.WorkBook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Outstanding Report');
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    } catch (error) {
      console.error('Export Error:', error);
    }
  }

//   printA4Landscape(): void {
//     const printSection = document.getElementById('print-section');
//     if (!printSection) return;
//     const printWindow = window.open('', '', 'width=1400,height=900');
//     if (!printWindow) return;

//     // ── Calculate scale ratio based on actual table width ──────────────────
//     const tableEl = printSection.querySelector('table');
//     const actualWidth = tableEl ? tableEl.scrollWidth : 1200;
//     const A4_WIDTH_PX = 1050; // ≈ 277mm at 96dpi
//     const scale = Math.min(1, A4_WIDTH_PX / actualWidth);

//     printWindow.document.write(`
// <html>
//   <head>
//     <title>जाहिरात येणे बाकी अहवाल</title>
//     <style>
//       @page {
//         size: A4 landscape;
//         margin: 5mm;
//       }

//       * {
//         -webkit-print-color-adjust: exact !important;
//         print-color-adjust: exact !important;
//         box-sizing: border-box;
//       }

//       html, body {
//         width: 277mm;
//         margin: 0;
//         padding: 0;
//         background: #fff;
//         font-family: Arial, sans-serif;
//         font-size: 9px;
//         color: #000;
//       }

//       /* ── Hide duplicate header inside printSection ── */
//       .report-header {
//         display: none !important;
//       }

//       /* ── Clean injected header ── */
//       .print-only-header {
//         text-align: center;
//         margin-bottom: 5px;
//         border-bottom: 2px solid #000;
//         padding-bottom: 4px;
//       }
//       .print-only-header h2 {
//         font-size: 13px;
//         font-weight: 900;
//         margin: 0 0 2px 0;
//         text-transform: uppercase;
//         letter-spacing: 1px;
//       }
//       .print-only-header p {
//         font-size: 9px;
//         font-weight: 700;
//         margin: 1px 0;
//       }

//       /* ── Scale wrapper ── */
//       .scale-wrapper {
//         width: ${Math.round(actualWidth * scale)}px;
//         transform: scale(${scale.toFixed(4)});
//         transform-origin: top left;
//         overflow: visible;
//       }

//       /* ── Table ── */
//       table {
//         border-collapse: collapse;
//         width: ${actualWidth}px;
//         table-layout: auto;
//       }

//       th, td {
//         border: 1px solid #000;
//         padding: 2px 4px;
//         font-size: 9px;
//         font-weight: bold;
//         color: #000;
//         background: #fff;
//         vertical-align: middle;
//         white-space: nowrap;
//         text-align: right;
//       }

//       td:first-child,
//       th:first-child {
//         text-align: left !important;
//         max-width: 130px;
//         overflow: hidden;
//         text-overflow: ellipsis;
//         white-space: nowrap;
//       }

//       /* ── Thead ── */
//       thead th {
//         background: #2ccdd3 !important;
//         color: #000 !important;
//         font-weight: 900;
//         font-size: 9px;
//         text-align: center !important;
//         border: 1px solid #000;
//       }

//       /* ── % columns — NOT bold ── */
//       .pct-header,
//       .pct-cell {
//         background: #f0f4ff !important;
//         color: #333 !important;
//         font-size: 8px;
//         font-weight: 400 !important;
//         text-align: center !important;
//         border-left: 1px dashed #999 !important;
//         white-space: nowrap;
//         padding: 2px 3px;
//       }

//       /* ── Grey subtotal columns ── */
//       .grey-cell {
//         background: #dce8f5 !important;
//         font-weight: 900;
//         border: 1px solid #000;
//         text-align: right !important;
//         white-space: nowrap;
//       }

//       /* ── Office total row ── */
//       .office-total-row td {
//         background: #c8e6c9 !important;
//         font-weight: 900;
//         border: 1px solid #000;
//         text-align: right !important;
//       }
//       .office-total-row td:first-child {
//         text-align: left !important;
//       }
//       .office-total-row .pct-cell {
//         background: #b2dfdb !important;
//         font-weight: 400 !important;
//         text-align: center !important;
//       }
//       .office-total-row .grey-cell {
//         background: #a5d6a7 !important;
//       }

//       /* ── Billing sub-row ── */
//       .billing-row td {
//         background: #fff8e1 !important;
//         font-size: 8px;
//         font-weight: 500;
//         color: #555 !important;
//         border-top: 1px dashed #ccc !important;
//         border-bottom: 1px solid #000 !important;
//         text-align: right !important;
//         white-space: nowrap;
//         padding: 2px 4px;
//       }
//       .billing-row td:first-child {
//         text-align: left !important;
//         font-style: italic;
//         color: #444 !important;
//       }
//       .billing-row .grey-cell {
//         background: #ffe082 !important;
//         font-weight: 700;
//         border: 1px solid #000 !important;
//       }

//       /* ── Grand total row ── */
//       .grand-total-row td {
//         background: #8eb7f3 !important;
//         color: #000 !important;
//         font-weight: 900;
//         font-size: 9px;
//         border: 1px solid #000;
//         text-align: right !important;
//       }
//       .grand-total-row td:first-child {
//         text-align: left !important;
//       }
//       .grand-total-row .pct-cell {
//         background: #bbcfee !important;
//         font-weight: 400 !important;
//         text-align: center !important;
//       }
//       .grand-total-row .grey-cell {
//         background: #6fa0e8 !important;
//       }

//       /* ── Grand billing row ── */
//       .grand-billing-row td {
//         background: #c5cae9 !important;
//         color: #000 !important;
//         font-weight: 900;
//         font-size: 9px;
//         border: 1px solid #000;
//         text-align: right !important;
//         white-space: nowrap;
//       }
//       .grand-billing-row td:first-child {
//         text-align: left !important;
//       }
//       .grand-billing-row .grey-cell {
//         background: #9fa8da !important;
//         border: 1px solid #000 !important;
//       }

//       /* ── Alternating rows ── */
//       tbody tr:nth-child(even) td { background: #f5f5f5 !important; }
//       tbody tr:nth-child(odd)  td { background: #fff    !important; }

//       /* ── Special rows override alternating ── */
//       .office-total-row  td { background: #c8e6c9 !important; }
//       .billing-row       td { background: #fff8e1 !important; }
//       .grand-total-row   td { background: #8eb7f3 !important; }
//       .grand-billing-row td { background: #c5cae9 !important; }

//       /* ── Keep outstanding + billing pair on same page ── */
//       .office-total-row,
//       .billing-row {
//         page-break-inside: avoid;
//       }

//       /* ── Hide buttons and spinners ── */
//       .noPrint, .actions, mat-spinner, .mat-spinner {
//         display: none !important;
//       }
//     </style>
//   </head>
//   <body>

//     <!-- Clean header -->
//     <div class="print-only-header">
//       <h2>जाहिरात येणे बाकी - ${this.offRepName}</h2>
//       <p>दिनांक: ${this.datePipe.transform(this.currentDate, 'dd-MM-yyyy')}</p>
//       <p>कालावधी: ${this.datePipe.transform(this.startDate, 'dd-MM-yyyy')} ते ${this.datePipe.transform(this.endDate, 'dd-MM-yyyy')}</p>
//     </div>

//     <!-- Scaled table wrapper -->
//     <div class="scale-wrapper">
//       ${printSection.innerHTML}
//     </div>

//   </body>
// </html>
//     `);

//     printWindow.document.close();
//     setTimeout(() => {
//       printWindow.print();
//       printWindow.close();
//     }, 600);
//   }


// printA4Landscape(): void {
//   const printSection = document.getElementById('print-section');
//   if (!printSection) return;

//   const printWindow = window.open('', '', 'width=1400,height=900');
//   if (!printWindow) return;

//   const tableEl = printSection.querySelector('table');
//   const actualWidth = tableEl ? tableEl.scrollWidth : 1200;

//   const A4_WIDTH_PX = 1050;
//   const scale = Math.min(1, A4_WIDTH_PX / actualWidth);

//   printWindow.document.write(`
// <html>
// <head>
// <title>जाहिरात येणे बाकी अहवाल</title>

// <style>
// @page {
//   size: A4 landscape;
//   margin: 5mm;
// }

// /* 🔥 Force BLACK text everywhere */
// * {
//   -webkit-print-color-adjust: exact !important;
//   print-color-adjust: exact !important;
//   color: #000 !important;
//   box-sizing: border-box;
// }

// html, body {
//   width: 277mm;
//   margin: 0;
//   padding: 0;
//   background: #fff;
//   font-family: Arial, sans-serif;
//   font-size: 9px;
// }

// /* Hide original header */
// .report-header {
//   display: none !important;
// }

// /* Header */
// .print-only-header {
//   text-align: center;
//   margin-bottom: 10px;
//   border-bottom: 2px solid #000;
//   padding-bottom: 6px;
// }

// .print-only-header h2 {
//   font-size: 14px;
//   font-weight: 900;
//   margin: 0;
// }

// .print-only-header p {
//   font-size: 10px;
//   font-weight: 700;
//   margin: 2px 0;
// }

// /* Scaling */
// .scale-wrapper {
//   width: ${Math.round(actualWidth * scale)}px;
//   transform: scale(${scale});
//   transform-origin: top left;
// }

// /* Table */
// table {
//   border-collapse: collapse;
//   width: ${actualWidth}px;
// }

// /* 🔥 Better padding + clean look */
// th, td {
//   border: 1px solid #000;
//   padding: 4px 6px;
//   font-size: 9px;
//   font-weight: 600;
//   background: #fff;
//   white-space: nowrap;
//   text-align: right;
// }

// /* First column left */
// td:first-child,
// th:first-child {
//   text-align: left !important;
//   max-width: 150px;
//   overflow: hidden;
//   text-overflow: ellipsis;
// }

// /* 🔥 Last column (एकुन जाहिरात व्यवसाय) RIGHT */
// td:last-child,
// th:last-child {
//   text-align: right !important;
//   font-weight: 900;
// }

// /* Header */
// thead th {
//   background: #2ccdd3 !important;
//   font-weight: 900;
//   text-align: center !important;
// }

// /* % columns */
// .pct-header,
// .pct-cell {
//   background: #f0f4ff !important;
//   font-size: 8px;
//   text-align: center !important;
// }

// /* Subtotal */
// .grey-cell {
//   background: #dce8f5 !important;
//   font-weight: 900;
//   text-align: right !important;
// }

// /* Office total */
// .office-total-row td {
//   background: #c8e6c9 !important;
//   font-weight: 900;
// }

// /* Billing row */
// .billing-row td {
//   background: #fff8e1 !important;
//   font-size: 8px;
// }

// /* Grand total */
// .grand-total-row td {
//   background: #8eb7f3 !important;
//   font-weight: 900;
// }

// /* Alternate rows */
// tbody tr:nth-child(even) td {
//   background: #f5f5f5 !important;
// }

// /* Avoid breaking rows */
// .office-total-row,
// .billing-row {
//   page-break-inside: avoid;
// }

// /* Hide unwanted */
// .noPrint, .actions {
//   display: none !important;
// }
// </style>
// </head>

// <body>

// <div class="print-only-header">
//   <h2>जाहिरात येणे बाकी - ${this.offRepName}</h2>
//   <p>दिनांक: ${this.datePipe.transform(this.currentDate, 'dd-MM-yyyy')}</p>
//   <p>कालावधी: ${this.datePipe.transform(this.startDate, 'dd-MM-yyyy')} ते ${this.datePipe.transform(this.endDate, 'dd-MM-yyyy')}</p>
// </div>

// <div class="scale-wrapper">
//   ${printSection.innerHTML}
// </div>

// </body>
// </html>
//   `);

//   printWindow.document.close();

//   setTimeout(() => {
//     printWindow.print();
//     printWindow.close();
//   }, 600);
// }

// printA4Landscape(): void {
//   const printSection = document.getElementById('print-section');
//   if (!printSection) return;

//   const printWindow = window.open('', '', 'width=1400,height=900');
//   if (!printWindow) return;

//   const tableEl = printSection.querySelector('table');
//   const actualWidth = tableEl ? tableEl.scrollWidth : 1200;

//   const A4_WIDTH_PX = 1050;
//   const scale = Math.min(1, A4_WIDTH_PX / actualWidth);

//   printWindow.document.write(`
// <html>
// <head>
// <title>जाहिरात येणे बाकी अहवाल</title>
// <style>
// @page {
//   size: A4 landscape;
//   margin: 5mm;
// }

// * {
//   -webkit-print-color-adjust: exact !important;
//   print-color-adjust: exact !important;
//   color: #000 !important;
//   box-sizing: border-box;
// }

// html, body {
//   width: 277mm;
//   margin: 0;
//   padding: 0;
//   background: #fff;
//   font-family: 'Arial', sans-serif;
// }

// /* ── Header Block ── */
// .print-only-header {
//   text-align: center;
//   margin-bottom: 8px;
//   padding-bottom: 6px;
//   border-bottom: 3px double #000;
// }

// .print-only-header h2 {
//   font-size: 16px;
//   font-weight: 900;
//   margin: 0 0 3px 0;
//   letter-spacing: 0.5px;
//   text-transform: uppercase;
// }

// .print-only-header .meta-row {
//   display: flex;
//   justify-content: center;
//   gap: 30px;
//   font-size: 10px;
//   font-weight: 700;
//   margin-top: 4px;
// }

// .print-only-header .meta-row span {
//   padding: 2px 10px;
//   border: 1px solid #333;
//   border-radius: 3px;
//   background: #f5f5f5 !important;
// }

// /* ── Scale Wrapper ── */
// .scale-wrapper {
//   width: ${Math.round(actualWidth * scale)}px;
//   transform: scale(${scale});
//   transform-origin: top left;
// }

// /* ── Table Base ── */
// table {
//   border-collapse: collapse;
//   width: ${actualWidth}px;
//   border: 2px solid #000;
// }

// /* ── All Cells ── */
// th, td {
//   border: 1px solid #555;
//   padding: 5px 7px;
//   font-size: 10px;
//   font-weight: 600;
//   white-space: nowrap;
//   text-align: right;
//   vertical-align: middle;
// }

// /* ── First Column (Name) ── */
// td:first-child,
// th:first-child {
//   text-align: left !important;
//   max-width: 160px;
//   overflow: hidden;
//   text-overflow: ellipsis;
//   font-weight: 700;
//   padding-left: 8px;
//   border-right: 2px solid #555;
// }

// /* ── Last Column (Total) ── */
// td:last-child,
// th:last-child {
//   text-align: right !important;
//   font-weight: 900;
//   border-left: 2px solid #555;
// }

// /* ── Table Header ── */
// thead tr {
//   background: #1a6b8a !important;
//   border-bottom: 2px solid #000;
// }

// thead th {
//   background: #1a6b8a !important;
//   color: #fff !important;
//   font-weight: 900;
//   font-size: 10px;
//   text-align: center !important;
//   padding: 6px 7px;
//   letter-spacing: 0.3px;
// }

// /* ── % (Percentage) Columns ── */
// .pct-header,
// .pct-cell {
//   background: #e8f4f8 !important;
//   font-size: 9px;
//   text-align: center !important;
//   color: #1a4a6b !important;
//   font-style: italic;
// }

// /* ── Subtotal / Grey Rows ── */
// .grey-cell {
//   background: #dce8f5 !important;
//   font-weight: 900;
//   text-align: right !important;
//   border-top: 1.5px solid #555 !important;
//   border-bottom: 1.5px solid #555 !important;
// }

// /* ── Office Total Row ── */
// .office-total-row td {
//   background: #d0ead0 !important;
//   font-weight: 900;
//   font-size: 10px;
//   border-top: 2px solid #333 !important;
//   border-bottom: 2px solid #333 !important;
// }

// /* ── Billing Row ── */
// .billing-row td {
//   background: #fff8e1 !important;
//   font-size: 9px;
//   font-style: italic;
//   color: #555 !important;
// }

// /* ── Grand Total Row ── */
// .grand-total-row td {
//   background: #3a6fc4 !important;
//   color: #fff !important;
//   font-weight: 900;
//   font-size: 11px;
//   border-top: 3px solid #000 !important;
//   padding: 7px 7px;
// }

// /* ── Alternating Row Colors ── */
// tbody tr:nth-child(odd) td {
//   background: #ffffff !important;
// }

// tbody tr:nth-child(even) td {
//   background: #f0f6fb !important;
// }

// /* ── Row Hover / Section Separator ── */
// tbody tr.section-start td {
//   border-top: 2px solid #333 !important;
// }

// /* ── Page Break Control ── */
// .office-total-row,
// .billing-row,
// .grand-total-row {
//   page-break-inside: avoid;
// }

// /* ── Hide Unwanted Elements ── */
// .noPrint, .actions, .report-header {
//   display: none !important;
// }
// </style>
// </head>

// <body>

// <div class="print-only-header">
//   <h2>जाहिरात येणे बाकी &nbsp;—&nbsp; ${this.offRepName}</h2>
//   <div class="meta-row">
//     <span>&#128197; दिनांक : ${this.datePipe.transform(this.currentDate, 'dd-MM-yyyy')}</span>
//     <span>&#128336; कालावधी : ${this.datePipe.transform(this.startDate, 'dd-MM-yyyy')} ते ${this.datePipe.transform(this.endDate, 'dd-MM-yyyy')}</span>
//   </div>
// </div>

// <div class="scale-wrapper">
//   ${printSection.innerHTML}
// </div>

// </body>
// </html>
//   `);

//   printWindow.document.close();

//   setTimeout(() => {
//     printWindow.print();
//     printWindow.close();
//   }, 600);
// }

printA4Landscape(): void {
  const printSection = document.getElementById('print-section');
  if (!printSection) return;

  const printWindow = window.open('', '', 'width=1400,height=900');
  if (!printWindow) return;

  const tableEl = printSection.querySelector('table');
  const actualWidth = tableEl ? tableEl.scrollWidth : 1200;

  const A4_WIDTH_PX = 1050;
  const scale = Math.min(1, A4_WIDTH_PX / actualWidth);

  printWindow.document.write(`
<html>
<head>
<title>जाहिरात येणे बाकी अहवाल</title>
<style>
@page {
  size: A4 landscape;
  margin: 5mm;
}

* {
  -webkit-print-color-adjust: exact !important;
  print-color-adjust: exact !important;
  color: #000 !important;
  box-sizing: border-box;
}

html, body {
  width: 277mm;
  margin: 0;
  padding: 0;
  background: #fff;
  font-family: 'Arial', sans-serif;
}

/* ── Header Block ── */
.print-only-header {
  text-align: center;
  margin-bottom: 10px;
  padding: 10px 0 10px 0;
  border-bottom: 3px double #000;
}

.print-only-header h2 {
  font-size: 20px;
  font-weight: 900;
  margin: 0 0 8px 0;
  letter-spacing: 1px;
  text-transform: uppercase;
}

.print-only-header .meta-row {
  display: flex;
  justify-content: center;
  gap: 30px;
  font-size: 11px;
  font-weight: 700;
  margin-top: 6px;
}

.print-only-header .meta-row span {
  padding: 5px 16px;
  border: 1.5px solid #333;
  border-radius: 4px;
  background: #f5f5f5 !important;
  line-height: 1.6;
}

/* ── Scale Wrapper ── */
.scale-wrapper {
  width: ${Math.round(actualWidth * scale)}px;
  transform: scale(${scale});
  transform-origin: top left;
}

/* ── Table Base ── */
table {
  border-collapse: collapse;
  width: ${actualWidth}px;
  border: 2px solid #000;
}

/* ── All Cells ── */
th, td {
  border: 1px solid #555;
  padding: 5px 7px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  text-align: right;
  vertical-align: middle;
}

/* ── First Column (Name) ── */
td:first-child,
th:first-child {
  text-align: left !important;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 700;
  padding-left: 8px;
  border-right: 2px solid #555;
}

/* ── Last Column (Total) ── */
td:last-child,
th:last-child {
  text-align: right !important;
  font-weight: 900;
  border-left: 2px solid #555;
}

/* ── Table Header ── */
thead tr {
  background: #1a6b8a !important;
  border-bottom: 2px solid #000;
}

thead th {
  background: #1a6b8a !important;
  color: #fff !important;
  font-weight: 900;
  font-size: 10px;
  text-align: center !important;
  padding: 6px 7px;
  letter-spacing: 0.3px;
}

/* ── % (Percentage) Columns ── */
.pct-header,
.pct-cell {
  background: #e8f4f8 !important;
  font-size: 9px;
  text-align: center !important;
  color: #1a4a6b !important;
  font-style: italic;
}

/* ── Subtotal / Grey Rows ── */
.grey-cell {
  background: #dce8f5 !important;
  font-weight: 900;
  text-align: right !important;
  border-top: 1.5px solid #555 !important;
  border-bottom: 1.5px solid #555 !important;
}

/* ── Office Total Row ── */
.office-total-row td {
  background: #d0ead0 !important;
  font-weight: 900;
  font-size: 10px;
  border-top: 2px solid #333 !important;
  border-bottom: 2px solid #333 !important;
}

/* ── Billing Row ── */
.billing-row td {
  background: #fff8e1 !important;
  font-size: 9px;
  font-style: italic;
  color: #555 !important;
}

/* ── Grand Total Row ── */
.grand-total-row td {
  background: #3a6fc4 !important;
  color: #fff !important;
  font-weight: 900;
  font-size: 11px;
  border-top: 3px solid #000 !important;
  padding: 7px 7px;
}

/* ── Alternating Row Colors ── */
tbody tr:nth-child(odd) td {
  background: #ffffff !important;
}

tbody tr:nth-child(even) td {
  background: #f0f6fb !important;
}

/* ── Row Hover / Section Separator ── */
tbody tr.section-start td {
  border-top: 2px solid #333 !important;
}

/* ── Page Break Control ── */
.office-total-row,
.billing-row,
.grand-total-row {
  page-break-inside: avoid;
}

/* ── Hide Unwanted Elements ── */
.noPrint, .actions, .report-header {
  display: none !important;
}
</style>
</head>

<body>

<div class="print-only-header">
  <h2>जाहिरात येणे बाकी &nbsp;—&nbsp; ${this.offRepName}</h2>
  <div class="meta-row">
    <span>&#128197; दिनांक : ${this.datePipe.transform(this.currentDate, 'dd-MM-yyyy')}</span>
    <span>&#128336; कालावधी : ${this.datePipe.transform(this.startDate, 'dd-MM-yyyy')} ते ${this.datePipe.transform(this.endDate, 'dd-MM-yyyy')}</span>
  </div>
</div>

<div class="scale-wrapper">
  ${printSection.innerHTML}
</div>

</body>
</html>
  `);

  printWindow.document.close();

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 600);
}

  checkDateSelection() {
    this.isDateSelected = !!(this.startDate && this.endDate);
  }

  onDateChange() {
    this.checkDateSelection();
  }

}